import * as yup from "yup"
import {UserFormDetails, UserTitle} from "@irati/payrollpay/utils/enum/UserDetails"
import {
  vivupPlaceOfWorkSchema,
  employeeNumberSchema,
  vivupNiNumberSchema,
  titleSchema,
  firstNameSchema,
  lastNameSchema,
  mobileNumberSchema,
  emailSchema,
  addressLine1Schema,
  townSchema,
  ukPostCodeSchema,
  dobDaySchema,
  dobMonthSchema,
  dobYearSchema
} from "@irati/payrollpay/utils/validation"

export const titleOptions = [
  {value: "mr", label: UserTitle.mr},
  {value: "mrs", label: UserTitle.mrs},
  {value: "miss", label: UserTitle.miss},
  {value: "ms", label: UserTitle.ms},
  {value: "dr", label: UserTitle.dr},
  {value: "prof", label: UserTitle.prof}
]

export const dayOptions = Array.from({length: 31}, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString()
}))

export const monthOptions = [
  {value: "1", label: "January"},
  {value: "2", label: "February"},
  {value: "3", label: "March"},
  {value: "4", label: "April"},
  {value: "5", label: "May"},
  {value: "6", label: "June"},
  {value: "7", label: "July"},
  {value: "8", label: "August"},
  {value: "9", label: "September"},
  {value: "10", label: "October"},
  {value: "11", label: "November"},
  {value: "12", label: "December"}
]

export const yearOptions = Array.from({length: 100}, (_, i) => ({
  value: (2024 - i).toString(),
  label: (2024 - i).toString()
}))

export type ComponentType = "input" | "select" | "checkbox" | "label" | "divider"

export interface CheckoutFormField {
  id: string
  name: string
  label?: string
  placeholder?: string
  component: ComponentType
  required?: boolean
  options?: {value: string; label: string}[]
  flex?: string
  validation?: yup.Schema<unknown>
  defaultValue?: unknown
}

export const CheckoutFormContent: CheckoutFormField[] = [
  {
    id: "placeOfWork",
    name: "placeOfWork",
    label: `${UserFormDetails.placeOfWork} *`,
    component: "input",
    required: true,
    defaultValue: "",
    validation: vivupPlaceOfWorkSchema
  },
  {
    id: "employeeNumber",
    name: "employeeNumber",
    label: `${UserFormDetails.empNumber} *`,
    component: "input",
    required: true,
    defaultValue: "",
    validation: employeeNumberSchema
  },
  {
    id: "niNumber",
    name: "niNumber",
    label: `${UserFormDetails.niNumber} *`,
    component: "input",
    required: true,
    defaultValue: "",
    validation: vivupNiNumberSchema
  },
  {
    id: "isTemporaryNI",
    name: "isTemporaryNI",
    label: "Check this box if your National Insurance number is temporary",
    component: "checkbox",
    defaultValue: false
  },
  {
    id: "divider_1",
    name: "_divider1",
    component: "divider"
  },
  {
    id: "title",
    name: "title",
    label: UserFormDetails.title,
    component: "select",
    options: titleOptions,
    flex: "0 0 150px",
    defaultValue: "",
    validation: titleSchema
  },
  {
    id: "firstName",
    name: "firstName",
    label: `${UserFormDetails.firstName} *`,
    component: "input",
    defaultValue: "",
    required: true,
    validation: firstNameSchema
  },
  {
    id: "lastName",
    name: "lastName",
    label: `${UserFormDetails.lastName} *`,
    component: "input",
    defaultValue: "",
    required: true,
    validation: lastNameSchema
  },
  {
    id: "dobLabel",
    name: "_dobLabel",
    label: `${UserFormDetails.dateOfBirth} *`,
    component: "label"
  },
  {
    id: "dobDay",
    name: "dobDay",
    label: "",
    placeholder: "Day",
    component: "select",
    options: dayOptions,
    defaultValue: "",
    validation: dobDaySchema
  },
  {
    id: "dobMonth",
    name: "dobMonth",
    label: "",
    placeholder: "Month",
    component: "select",
    options: monthOptions,
    defaultValue: "",
    validation: dobMonthSchema
  },
  {
    id: "dobYear",
    name: "dobYear",
    label: "",
    placeholder: "Year",
    component: "select",
    options: yearOptions,
    defaultValue: "",
    validation: dobYearSchema
  },
  {
    id: "email",
    name: "email",
    label: `${UserFormDetails.email} *`,
    component: "input",
    defaultValue: "",
    required: true,
    validation: emailSchema
  },
  {
    id: "mobileNumber",
    name: "mobileNumber",
    label: `${UserFormDetails.mobileNumber} *`,
    placeholder: "Enter number starting with 0",
    component: "input",
    defaultValue: "",
    required: true,
    validation: mobileNumberSchema
  },
  {
    id: "divider_2",
    name: "_divider2",
    component: "divider"
  },
  {
    id: "addressLine1",
    name: "addressLine1",
    label: `${UserFormDetails.addressLineOne} *`,
    component: "input",
    defaultValue: "",
    required: true,
    validation: addressLine1Schema
  },
  {
    id: "addressLine2",
    name: "addressLine2",
    label: UserFormDetails.addressLineTwo,
    component: "input",
    defaultValue: ""
  },
  {
    id: "town",
    name: "town",
    label: `${UserFormDetails.town} *`,
    component: "input",
    defaultValue: "",
    required: true,
    validation: townSchema
  },
  {
    id: "county",
    name: "county",
    label: UserFormDetails.county,
    component: "input",
    defaultValue: ""
  },
  {
    id: "postcode",
    name: "postcode",
    label: `${UserFormDetails.postCode} *`,
    component: "input",
    defaultValue: "",
    required: true,
    validation: ukPostCodeSchema
  }
]

export const validationSchema = yup.object(
  CheckoutFormContent.reduce(
    (acc, field) => {
      if (field.validation) {
        acc[field.name] = field.validation
      }
      return acc
    },
    {} as Record<string, yup.Schema<unknown>>
  )
)

export const defaultFormValues = CheckoutFormContent.reduce(
  (acc, field) => {
    acc[field.name] = field.defaultValue ?? (field.component === "checkbox" ? false : "")
    return acc
  },
  {} as Record<string, unknown>
) as any
